import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const WaClientPortfolioDetailsPage = () => {

  const waApi = useWaApi();
  // Only a SYSTEM_ADMIN may change anything here; everyone else gets a read-only view. The service refuses
  // the writes too, so this just keeps the controls out of sight.
  const { canManageClientSetup } = useUserProfileAccess();
  const [allPortfolios, setAllPortfolios] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newPortfolio, setNewPortfolio] = useState({
    clientBusinessPortfolioId: '',
    clientDetailId: '',
    status: false,
  });

  const initialFilters = {
    id: '',
    clientBusinessPortfolioId: '',
    status: null
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(0);
  };

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(3);
  const [formError, setFormError] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);
  const [actionErrors, setActionErrors] = useState({});

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    setFormError({});
  };

  useEffect(() => {

    const fetchPortfolios = async () => {
      try {
        // WAClientPortfolioDetailController only exposes a cached full list, not a paged
        // search, so filtering and pagination for this page are done client-side.
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_CLIENT_PORTFOLIO_DETAILS_ENDPOINT
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setAllPortfolios(object || []);
        } else {
          setError(`Error: ${message}`);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching client portfolio details:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);
      }
    };

    const fetchClients = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_CLIENT_DETAILS_ENDPOINT
        );

        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) {
          setClients(object || []);
        } else {
          setError(`Error: ${message}`);
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError(err.message || 'Failed to load clients.');
      }
    };

    fetchPortfolios();
    fetchClients();
  }, [waApi]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
    }
  }, [modalOpen]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;

    setNewPortfolio((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => ({ ...prev, status: statusValue }));
  };

  const applyFilters = () => {
    clearMessages();
    setAppliedFilters(filters);
    setPage(0);
  };

  const filteredPortfolios = allPortfolios.filter((portfolio) => {
    if (appliedFilters.id && String(portfolio.id) !== String(appliedFilters.id)) return false;
    if (appliedFilters.clientBusinessPortfolioId && !portfolio.clientBusinessPortfolioId?.toLowerCase().includes(appliedFilters.clientBusinessPortfolioId.toLowerCase())) return false;
    if (appliedFilters.status !== null && portfolio.status !== appliedFilters.status) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredPortfolios.length / size));
  const paginatedPortfolios = filteredPortfolios.slice(page * size, page * size + size);

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WA_CLIENT_PORTFOLIO_DETAIL_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (id, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus;
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_CLIENT_PORTFOLIO_DETAIL_STATUS_ENDPOINT}/${id}`,
        { status: newStatus }
      );
      setAllPortfolios((prev) =>
        prev.map((portfolio) => portfolio.id === id ? { ...portfolio, status: newStatus } : portfolio)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client portfolio detail?')) {
      return;
    }
    setActionErrors((prev) => ({ ...prev, [id]: null }));
    try {
      await waApi.delete(`${process.env.REACT_APP_DELETE_WA_CLIENT_PORTFOLIO_DETAIL_ENDPOINT}/${id}`);
      setAllPortfolios((prev) => prev.filter((portfolio) => portfolio.id !== id));
      setActionErrors((prev) => ({ ...prev, [id]: null }));
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while deleting the client portfolio detail.' }));
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    const validation = validateRequiredFields({
      clientBusinessPortfolioId: { value: newPortfolio.clientBusinessPortfolioId, label: "Client Business Portfolio Id" },
      clientDetailId: { value: newPortfolio.clientDetailId, label: "Client" },
    });
    if (Object.keys(validation.errors).length > 0) {
      setFormError(validation.errors);
      return;
    }
    try {
      const selectedClientDetail = clients.find((client) => String(client.id) === String(newPortfolio.clientDetailId));
      const payload = deepTrim({
        clientBusinessPortfolioId: newPortfolio.clientBusinessPortfolioId,
        status: newPortfolio.status,
        waClientDetail: selectedClientDetail
      });
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WA_CLIENT_PORTFOLIO_DETAIL_ENDPOINT}`,
        payload
      );
      if (response.data.httpStatus === 200) {
        setAllPortfolios((prev) => [...prev, response.data.object]);
        setNewPortfolio({ clientBusinessPortfolioId: '', clientDetailId: '', status: true });
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while creating the client portfolio detail.');
    } finally {
      setModalOpen(false);
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  const handleSizeChange = (e) => {
    setSize(Number(e.target.value));
    setPage(0);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading client portfolio details: {error}</div>;

  return (
    <div className="page-container">
      <h1>App's Client Portfolio Details</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>
      <h3>Filters</h3>
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>Client Business Portfolio Id:</label>
          <input
            type="text"
            name="clientBusinessPortfolioId"
            value={filters.clientBusinessPortfolioId ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={applyFilters}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {canManageClientSetup && (
        <div className="action-bar">
          <button
            onClick={() => {
              clearMessages();
              setNewPortfolio({ clientBusinessPortfolioId: '', clientDetailId: '', status: false });
              setModalOpen(true);
            }}
          >
            + Create New
          </button>

          <button type="button" onClick={refreshCache}>
            Refresh Cache
          </button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">
            <h3>Create New Client Portfolio Detail</h3>
            <div className="form-group">
              <label>Client Business Portfolio Id:</label>
              <input
                type="text"
                name="clientBusinessPortfolioId"
                value={newPortfolio.clientBusinessPortfolioId}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Client:</label>
              <select
                name="clientDetailId"
                value={newPortfolio.clientDetailId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.clientName} ({client.clientId})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newPortfolio.status}
                onChange={handleInputChange}
              />
            </div>
            <button type="submit">Create Client Portfolio</button>
          </form>
        </div>
      </Modal>

      <h3>Existing Client Portfolio Details</h3>
      {paginatedPortfolios.length === 0 ? (
        <p>No client portfolio details available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Client Business Portfolio Id</th>
                <th>Client Portfolio</th>
                <th>Client Id</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                {canManageClientSetup && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedPortfolios.map((portfolio) => (
                <tr key={portfolio.id}>
                  <td>{portfolio.id}</td>
                  <td>{portfolio.clientBusinessPortfolioId}</td>
                  <td>{portfolio.waClientDetail?.clientName}</td>
                  <td>{portfolio.waClientDetail?.clientId}</td>
                  <td>{portfolio.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(portfolio.createdTimestamp).toLocaleString()}</td>
                  <td>{new Date(portfolio.updatedTimestamp).toLocaleString()}</td>
                  <td>{portfolio.lastUpdatedBy}</td>
                  {canManageClientSetup && (
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => toggleActivationStatus(portfolio.id, portfolio.status)}>
                          {portfolio.status ? 'Disable' : 'Enable'}
                        </button>
                        {/* <button onClick={() => handleDelete(portfolio.id)}>Delete</button> */}
                      </div>
                      {actionErrors[portfolio.id] && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                          {actionErrors[portfolio.id]}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WaClientPortfolioDetailsPage;
